import React, { useContext, useState } from 'react'
import axios from 'axios'
import qs from 'qs'
import 'jspdf-autotable'
import AppContext from 'context'
import jsPDF from 'jspdf'
import WP_DATA from 'utils/wp_data'
import { moneyFormat, timestampFromString, dateFromTimestamp, normalCase } from 'utils/helpers'
import Logo from 'img/logo.png'

const blueRGB = [8, 90, 166]
const greenRGB = [124, 198, 83]
const greenLightRGB = [200, 233, 181]

export default props => {
  const { state } = useContext(AppContext)
  const [loading, setLoading] = useState(false)

  const generatePDF = async () => {
    setLoading(true)
    const doc = new jsPDF({ orientation: 'portrait' })
    const docWidth = doc.internal.pageSize.getWidth()
    // const docHeight = doc.internal.pageSize.getHeight()
    const initialFontSize = 10
    doc.setFontSize(initialFontSize)
    const disclosure = WP_DATA.disclosure

    let lineHeight = (doc.getLineHeight(disclosure) / doc.internal.scaleFactor)

    const initialYPos = 10
    let yPos = initialYPos

    // logo
    doc.addImage(
      Logo,
      'png',
      docWidth - 60,
      5,
      50,
      17
    )
    
    doc.setFontSize(16)
    let splitTitle = doc.splitTextToSize(state.title, 130)
    doc.text(splitTitle, 15, yPos)
    yPos+= splitTitle.length > 1 ? 12 : 5
    doc.setFontSize(initialFontSize)

    const printDate = dateFromTimestamp(timestampFromString(new Date().toDateString()))

    let { user_nicename, first_name, last_name} = WP_DATA.user.data
    let name 
    if (!!first_name && !!last_name) 
      name = `${first_name} ${last_name}`
    else
      name = user_nicename
    
    doc.text(name.toUpperCase(), 15, yPos)
    yPos += lineHeight
    doc.text('Printed: ' + printDate, 15, yPos)
    yPos += lineHeight

    doc.setFontSize(initialFontSize - 4)

    const contactInfo = `
      114 Main Street, PO Box 427
      Kewaskum, WI 53040
      Phone: (262) 626-8892
      Fax: (262) 626-2290
      Email: Hello@SGAdvisor.net
    `
    doc.text(contactInfo, docWidth - 54, 29)
  
    yPos += 5

    splitTitle = doc.splitTextToSize(disclosure, 130)
    doc.text(splitTitle, 15, yPos)
    yPos += (doc.getTextDimensions(splitTitle).h / 1.5) + 10

    doc.setFont('sans-serif', 'bold')
    doc.setFontSize(initialFontSize)

    // CASHFLOW ANALYSIS
    if (WP_DATA.worksheet_type === 'cashflow-analysis') {
      const head = ['ITEM', 'CURRENT', 'PROJECTED', 'NOTES']

      const grandTotals = {
        current: 0,
        projected: 0,
        sections: {},
        totalExpenses: {
          current: 0,
          projected: 0,
        },
      }

      Object.entries(state.worksheet_data).forEach(section => {
        const [sectionId, items] = section
        yPos +=  10

        const sectionTitle = sectionId.toUpperCase()
        doc.text(sectionTitle, 15, yPos)
        yPos +=  5
        
        const colTotals = {
          current: 0,
          projected: 0,
        }

        grandTotals.sections[sectionTitle] = {
          current: 0,
          projected: 0
        }

        const body = Object.entries(items).map(item => {
          const [itemName, fields] = item
          const { 
            current,
            projected,
            frequency,
            notes,
            // Retirement
            pre_tax_contributions,
            after_tax_contributions,
            // soc. security
            age_started,
            anticipated_62,
            anticipated_67,
            anticipated_70,
            // pension
            survivorship,
            survivorship_type,
            // income - salary
            salary_type,
            num_weekly_hours,
            avg_weekly_overtime_hours,
            // loans
            interest_rate,
            years_remaining,
            outstanding_balance,
            fixed_or_variable,
            terms,
          } = fields

          const finalCurrent = frequency === 'Annually' ? current / 12 : current
          const finalProjected = frequency === 'Annually' ? projected / 12 : projected

          colTotals.current += finalCurrent
          colTotals.projected += finalProjected

          if (sectionId === 'Income') {
            grandTotals.current += finalCurrent
            grandTotals.projected += finalProjected
          } else {
            grandTotals.current -= finalCurrent
            grandTotals.projected -= finalProjected
            grandTotals.totalExpenses.current += finalCurrent
            grandTotals.totalExpenses.projected += finalProjected
          }

          grandTotals.sections[sectionTitle].current += finalCurrent
          grandTotals.sections[sectionTitle].projected += finalProjected

          let notesCell = notes
          if (typeof pre_tax_contributions !== 'undefined') {
            notesCell += `\nPre Tax Contributions: ${pre_tax_contributions}`
          }
          if (typeof after_tax_contributions !== 'undefined') {
            notesCell += `\nAfter Tax Contributions: ${after_tax_contributions}`
          }
          if (typeof age_started !== 'undefined') {
            notesCell += `\nAge Started: ${age_started}`
          }
          if (typeof anticipated_62 !== 'undefined') {
            notesCell += `\nAnticipated at age 62: ${anticipated_62}`
          }
          if (typeof anticipated_67 !== 'undefined') {
            notesCell += `\nAnticipated at age 67: ${anticipated_67}`
          }
          if (typeof anticipated_70 !== 'undefined') {
            notesCell += `\nAnticipated at age 70: ${anticipated_70}`
          }
          if (typeof survivorship !== 'undefined') {
            notesCell += survivorship_type === '$'
              ? `\nSurvivorship:$${survivorship}`
              : `\nSurvivorship: ${survivorship}%`
          }

          // Salary
          if (typeof salary_type !== 'undefined') {
            notesCell += `\nSalary Type: ${salary_type}`
          }
          if (typeof num_weekly_hours !== 'undefined') {
            notesCell += `\nWeekly Hours: ${num_weekly_hours}`
          }
          if (typeof avg_weekly_overtime_hours !== 'undefined') {
            notesCell += `\nAvg. Weekly Overtime Hours: ${avg_weekly_overtime_hours}`
          }

          // loans 
          if (typeof interest_rate !== 'undefined') {
            notesCell += `\nInterest Rate: ${interest_rate}`
          }
          if (typeof years_remaining !== 'undefined') {
            notesCell += `\nYears Remaining: ${years_remaining}`
          }
          if (typeof outstanding_balance !== 'undefined') {
            notesCell += `\nOutstanding Balance: ${outstanding_balance}`
          }
          if (typeof fixed_or_variable !== 'undefined') {
            notesCell += `\nFixed or Variable: ${fixed_or_variable}`
          }
          if (typeof terms !== 'undefined') {
            notesCell += `\nTerms: ${terms}`
          }

          return [itemName, `${moneyFormat.to(finalCurrent)}/mo`, `${moneyFormat.to(finalProjected)}/mo`, notesCell]
        })

        const differenceDisplay = colTotals.projected !== 0 && colTotals.projected !== colTotals.current
          ? `Difference: ${moneyFormat.to(colTotals.projected - colTotals.current)}`
          : ''

        body.push(['Totals', moneyFormat.to(colTotals.current), moneyFormat.to(colTotals.projected), differenceDisplay])

        doc.autoTable({ 
          head: [head],
          body,
          startY: yPos,
          theme: 'grid',
          headStyles: {
            fillColor: sectionId === 'Income' ? greenRGB : blueRGB,
          },
          columnStyles: { 
            0: { cellWidth: 45 },
            1: { cellWidth: 45 },
            2: { cellWidth: 45 },
            3: { cellWidth: 45 },
          },
          didParseCell: function(data) {
            if (data.row.raw[0] === 'Totals') 
              data.cell.styles.fontStyle = 'bold'
            if (data.section === 'body' && data.column.index !== 0)
              data.cell.styles.halign = 'right'
          }
        })
        yPos = doc.previousAutoTable.finalY
      })
      yPos = doc.previousAutoTable.finalY+ 10

      doc.text('GRAND TOTALS', 15, yPos)
      yPos +=  5

      const sectionTotals = Object.entries(grandTotals.sections)
        .map(([k, v]) => [k, moneyFormat.to(v.current), moneyFormat.to(v.projected), moneyFormat.to(v.projected - v.current)])

      const body = [
        ...sectionTotals,
        [
          'TOTAL EXPENSES', 
          moneyFormat.to(grandTotals.totalExpenses.current), 
          moneyFormat.to(grandTotals.totalExpenses.projected), 
          moneyFormat.to(grandTotals.totalExpenses.projected - grandTotals.totalExpenses.current)
        ],
        [
          'TOTALS',
          moneyFormat.to(grandTotals.current),
          moneyFormat.to(grandTotals.projected),
          moneyFormat.to(grandTotals.projected - grandTotals.current)
        ]
      ]

      doc.autoTable({ 
        head: [['CATEGORY', 'CURRENT', 'PROJECTED', 'DIFFERENCE']],
        body,
        startY: yPos,
        theme: 'grid',
        headStyles: {
          fillColor: [8, 90, 166],
        },
        didParseCell: function(data) {
          if (data.row.raw[0] === 'INCOME') 
            data.cell.styles.fillColor = [200, 233, 181]
          if (data.row.raw[0] === 'TOTALS' || data.row.raw[0] === 'TOTAL EXPENSES') 
            data.cell.styles.fontStyle = 'bold'
          if (data.section === 'body' && data.column.index !== 0)
            data.cell.styles.halign = 'right'
        }
      })

    } 
    // ASSET ANALYSIS
    else if (WP_DATA.worksheet_type === 'asset-analysis') {
      const head = ['ITEM', 'SELF', 'SPOUSE', 'JOINT', 'TOTAL', 'NOTES']

      const netWorth = {
        self: 0,
        spouse: 0,
        joint: 0,
        sections: {},
      }

      Object.entries(state.worksheet_data).forEach((section, i) => {
        const [sectionId, items] = section
        const sectionTitle = sectionId.toUpperCase()
        yPos +=  10
        doc.text(sectionId.toUpperCase(), 15, yPos)
        yPos +=  5

        const colTotals = {
          self: 0,
          spouse: 0,
          joint: 0,
          rowTotal: 0,
        }

        const body = Object.entries(items).map(item => {
          const [itemName, fields] = item
          const { self, spouse, joint, notes, death_benefit } = fields
          const rowTotal = self + spouse + joint

          netWorth.sections[sectionTitle] = {
            self: 0,
            spouse: 0,
            joint: 0,
            rowTotal: 0,
          }
          
          if (sectionId === 'Liabilities') {
            colTotals.self -= self
            colTotals.spouse -= spouse
            colTotals.joint -= joint
            colTotals.rowTotal += rowTotal
          } else {
            colTotals.self += self
            colTotals.spouse += spouse
            colTotals.joint += joint
            colTotals.rowTotal += rowTotal
          }

          let notesCell = notes
          if (typeof death_benefit !== 'undefined') {
            notesCell += `\nDeath Benefit: ${death_benefit}`
          }

          return [itemName, moneyFormat.to(self), moneyFormat.to(spouse), moneyFormat.to(joint), moneyFormat.to(rowTotal), notesCell]
        })

        netWorth.self += colTotals.self
        netWorth.spouse += colTotals.spouse
        netWorth.joint += colTotals.joint
        netWorth.sections[sectionTitle].self += colTotals.self
        netWorth.sections[sectionTitle].spouse += colTotals.spouse
        netWorth.sections[sectionTitle].joint += colTotals.joint
        netWorth.sections[sectionTitle].rowTotal += colTotals.self + colTotals.spouse + colTotals.joint

        body.push(['Totals', moneyFormat.to(Math.abs(colTotals.self)), moneyFormat.to(Math.abs(colTotals.spouse)), moneyFormat.to(Math.abs(colTotals.joint)), moneyFormat.to(Math.abs(colTotals.rowTotal)), ''])

        doc.autoTable({ 
          head: [head],
          body: body,
          startY: yPos,
          theme: 'grid',
          headStyles: {
            fillColor: sectionId === 'Liabilities' ? blueRGB : greenRGB,
          },
          columnStyles: { 
            0: { cellWidth: 20 },
            1: { cellWidth: 30 },
            2: { cellWidth: 30 },
            3: { cellWidth: 30 },
            4: { cellWidth: 30 },
            5: { cellWidth: 20 },
          },
          createdCell: function(cell) {
            if (cell.section === 'body' && cell.column.index !== 0)
              cell.cell.styles.halign = 'right'
            if (cell.column.raw.header === 'TOTAL' && cell.section === 'body')
              cell.cell.styles.fillColor = '#eee'
            else if (cell.row.raw[0] === 'Totals') 
              cell.cell.styles.fontStyle = 'bold'            
          }
        })
        yPos = doc.previousAutoTable.finalY
      })
      yPos = doc.previousAutoTable.finalY + 10

      const sectionTotals = Object.entries(netWorth.sections)
      .map(([k, v]) => [k, moneyFormat.to(v.self), moneyFormat.to(v.spouse), moneyFormat.to(v.joint), moneyFormat.to(v.rowTotal)])

      const body = [
        ...sectionTotals,
        ['TOTALS', moneyFormat.to(netWorth.self), moneyFormat.to(netWorth.spouse), moneyFormat.to(netWorth.joint), moneyFormat.to(netWorth.self + netWorth.spouse + netWorth.joint)]
      ]

      doc.text('NET WORTH', 15, yPos)
      yPos +=  5
      doc.autoTable({ 
        head: [['CATEGORY', 'SELF', 'SPOUSE', 'JOINT', 'TOTAL']],
        body: body,
        startY: yPos,
        theme: 'grid',
        headStyles: {
          fillColor: [8, 90, 166],
        },
        didParseCell: function(data) {
          if (data.section === 'body' && (data.row.raw[0] !== 'LIABILITIES' && data.row.raw[0] !== 'TOTALS')) 
            data.cell.styles.fillColor = greenLightRGB
          if (data.row.raw[0] === 'TOTALS') 
            data.cell.styles.fontStyle = 'bold'
          if (data.section === 'body' && data.column.index !== 0)
            data.cell.styles.halign = 'right'
        }
      })
    }

    doc.setFont('sans-serif', 'normal')

    yPos = doc.previousAutoTable.finalY + 10

    // Add page numbers to footer
    const pages = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.width;  //Optional
    const pageHeight = doc.internal.pageSize.height;  //Optional
    doc.setFontSize(10);  //Optional
    for (let j = 1; j < pages + 1 ; j++) {
      // let horizontalPos = pageWidth / 2;  //Can be fixed number
      let verticalPos = pageHeight - 10;  //Can be fixed number
      doc.setPage(j);
      doc.text(`${j} of ${pages}`, pageWidth - 15, verticalPos, {align: 'right'});
    }

    doc.save('report.pdf')
    
    if (process.env.NODE_ENV === 'production') {
      try {
        const params = qs.stringify({
          action: 'sg_client_portal_actions',
          do: 'notify_sg',
          permalink: WP_DATA.permalink,
          display_name: WP_DATA.user.data.display_name,
        })
        const response = await axios.post(WP_DATA.admin_ajax_url, params)
        console.log(response)
      } catch(err) {
        console.log(err)
      }
    }

    setLoading(false)
  }

  return (
    <div onClick={generatePDF} id="report">
      { loading ? (
        <>Generating...</>
      ) : props.children }
    </div>
  )
}